/** Absolute mode of the TimeWindowPanel.
 *
 * The From/To fields hold the value (typed, to the second; ↑/↓ nudge the focused part).
 * The calendar and the shortcuts are fast ways to fill them: one click on a day selects
 * that whole day, a second click extends the window through the end of the clicked
 * day. "Now ⟳" turns To into a live `now`, which makes "since 14:00, live".
 *
 * Drafts live in wall-clock space (what the fields show in the selected zone) and are
 * converted when the zone changes and at Apply, so toggling the zone re-labels the same
 * instants instead of drifting them.
 */
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UpdateIcon from '@mui/icons-material/Update';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { DateRangeCalendar, DateTimeField } from '@mui/x-date-pickers-pro';
import type { DateRange, RangePosition } from '@mui/x-date-pickers-pro';
import { startOfDay } from 'date-fns';
import { useState, type ComponentProps, type ReactNode } from 'react';

import { formatDuration, windowLabel } from './labels';
import { NOW } from './relative';
import { instantToWallClock, wallClockToInstant } from './timezone';
import type { TimeWindowTz } from './types';

export const FIELD_FORMAT = 'yyyy-MM-dd HH:mm:ss';

/** A fixed To, or the live `now`. */
export type AbsoluteTo = Date | 'now' | null;

interface Props {
  /** Instants (not wall clocks). */
  initialFrom: Date | null;
  initialTo: AbsoluteTo;
  tz: TimeWindowTz;
  requireBothBounds: boolean;
  /** Offer "Now ⟳" (a live To). False where the result is frozen anyway. */
  allowLive: boolean;
  onApply: (from: Date | null, to: AbsoluteTo) => void;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function isUsable(d: Date | null): boolean {
  return d === null || !Number.isNaN(d.getTime());
}

function endOfDayToSecond(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
}

function toSecond(d: Date): Date {
  return new Date(Math.floor(d.getTime() / 1000) * 1000);
}

function relabel(wall: Date | null, from: TimeWindowTz, to: TimeWindowTz): Date | null {
  return wall && isUsable(wall) ? instantToWallClock(wallClockToInstant(wall, from), to) : wall;
}

export function AbsolutePane({
  initialFrom,
  initialTo,
  tz,
  requireBothBounds,
  allowLive,
  onApply,
}: Props) {
  const [from, setFrom] = useState<Date | null>(() =>
    initialFrom ? instantToWallClock(initialFrom, tz) : null,
  );
  const [to, setTo] = useState<Date | null>(() =>
    initialTo instanceof Date ? instantToWallClock(initialTo, tz) : null,
  );
  const [toLive, setToLive] = useState(allowLive && initialTo === 'now');
  const [rangePosition, setRangePosition] = useState<RangePosition>('start');

  // Re-label drafts when the zone toggles (state adjusted during render, not in an effect).
  const [draftTz, setDraftTz] = useState(tz);
  if (draftTz !== tz) {
    setDraftTz(tz);
    setFrom((f) => relabel(f, draftTz, tz));
    setTo((t) => relabel(t, draftTz, tz));
  }

  const nowWall = () => instantToWallClock(toSecond(new Date()), tz);
  // Arithmetic happens on instants, so a wall-clock shift never lands in a DST gap.
  const shift = (wall: Date, ms: number) =>
    instantToWallClock(new Date(wallClockToInstant(wall, tz).getTime() + ms), tz);

  const setFixedTo = (d: Date | null) => {
    setTo(d);
    setToLive(false);
  };

  const onCalendarChange = (range: DateRange<Date>) => {
    const clicked = rangePosition === 'start' ? range[0] : (range[1] ?? range[0]);
    if (!clicked) return;
    if (rangePosition === 'start' || !from || !isUsable(from)) {
      setFrom(startOfDay(clicked));
      setFixedTo(endOfDayToSecond(clicked));
      setRangePosition('end');
    } else if (clicked < startOfDay(from)) {
      // A day before From extends the window backwards; To stays.
      setFrom(startOfDay(clicked));
      setRangePosition('start');
    } else {
      setFixedTo(endOfDayToSecond(clicked));
      setRangePosition('start');
    }
  };

  const usable = isUsable(from) && isUsable(to);
  const fixedTo = toLive ? null : to;
  const inverted = usable && !!from && !!fixedTo && from.getTime() > fixedTo.getTime();
  const filled = requireBothBounds ? !!from && (toLive || !!to) : !!from || !!to;
  const canApply = usable && !inverted && filled && !(toLive && !from);

  const fromInstant = from && isUsable(from) ? wallClockToInstant(from, tz) : null;
  const toInstant = fixedTo && isUsable(fixedTo) ? wallClockToInstant(fixedTo, tz) : null;

  const apply = () => {
    if (canApply) onApply(fromInstant, toLive ? 'now' : toInstant);
  };

  let readout: ReactNode;
  if (!usable) {
    readout = <Problem>Finish typing the date and time</Problem>;
  } else if (inverted) {
    readout = <Problem>From must be before To</Problem>;
  } else if (!filled || (toLive && !from)) {
    readout = (
      <Typography variant="body2" color="text.secondary">
        {requireBothBounds ? 'Pick a day, or type From and To' : 'Pick a day, or type From or To'}
      </Typography>
    );
  } else {
    // The span a To covers runs to the end of its second (the end-bound rule).
    const end = toLive ? Date.now() : toInstant ? toInstant.getTime() + 1000 : null;
    const label = windowLabel(fromInstant, toLive ? NOW : toInstant, { tz, zone: true });
    readout = (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {fromInstant && end !== null && (
          <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {formatDuration(end - fromInstant.getTime())}
            {' · '}
          </Box>
        )}
        {label}
        {toLive && ', live'}
      </Typography>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        apply();
      }}
    >
      <Box
        sx={{
          display: 'grid',
          // Side by side from a tablet up; stacked on a phone.
          gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
          columnGap: 1,
          rowGap: 1.5,
          alignItems: 'start',
        }}
      >
        <Box>
          <DateTimeField
            label="From"
            value={from}
            onChange={(d) => setFrom(d)}
            format={FIELD_FORMAT}
            ampm={false}
            size="small"
            clearable
            fullWidth
            sx={TABULAR}
          />
          <Shortcuts label="From shortcuts">
            <Shortcut
              name="From: start of day"
              onClick={() => setFrom(startOfDay(from && isUsable(from) ? from : nowWall()))}
            >
              Start of day
            </Shortcut>
            <Shortcut name="From: now" onClick={() => setFrom(nowWall())}>
              Now
            </Shortcut>
          </Shortcuts>
        </Box>

        <ArrowForwardIcon
          fontSize="small"
          sx={{ color: 'text.secondary', mt: 1.25, display: { xs: 'none', sm: 'block' } }}
          aria-hidden
        />

        <Box>
          {toLive ? (
            <LiveTo onFix={() => setFixedTo(nowWall())} />
          ) : (
            <DateTimeField
              label="To"
              value={to}
              onChange={(d) => setTo(d)}
              format={FIELD_FORMAT}
              ampm={false}
              size="small"
              clearable
              fullWidth
              sx={TABULAR}
            />
          )}
          <Shortcuts label="To shortcuts">
            {(
              [
                ['+15m', '15 minutes', 15 * MINUTE],
                ['+1h', '1 hour', HOUR],
                ['+1d', '1 day', DAY],
              ] as const
            ).map(([text, words, ms]) => (
              <Shortcut
                key={text}
                name={`To: From + ${words}`}
                disabled={!from || !isUsable(from)}
                onClick={() => from && setFixedTo(shift(from, ms))}
              >
                {text}
              </Shortcut>
            ))}
            <Shortcut
              name="To: end of From's day"
              onClick={() =>
                setFixedTo(endOfDayToSecond(from && isUsable(from) ? from : nowWall()))
              }
            >
              End of day
            </Shortcut>
            {allowLive ? (
              <Shortcut
                name="To: now, live — moves with every refresh"
                startIcon={<UpdateIcon />}
                onClick={() => setToLive(true)}
              >
                Now
              </Shortcut>
            ) : (
              <Shortcut name="To: now" onClick={() => setFixedTo(nowWall())}>
                Now
              </Shortcut>
            )}
          </Shortcuts>
        </Box>
      </Box>

      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, alignItems: 'flex-start', mt: 1 }}
      >
        <DateRangeCalendar
          calendars={1}
          value={[
            from && isUsable(from) ? from : null,
            toLive ? nowWall() : fixedTo && isUsable(fixedTo) ? fixedTo : null,
          ]}
          onChange={onCalendarChange}
          rangePosition={rangePosition}
          onRangePositionChange={() => {}}
          disableDragEditing
          reduceAnimations
          sx={{
            width: 288,
            mx: -1,
            '& .MuiPickersCalendarHeader-root': { mt: 0.5, pl: 2 },
            '& .MuiDayCalendar-monthContainer': { fontVariantNumeric: 'tabular-nums' },
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ pt: { xs: 0, sm: '58px' }, maxWidth: 190 }}
        >
          Click a day for that whole day. Click another day to run through the end of it.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, minHeight: 32 }}>
        <Box sx={{ flex: 1, minWidth: 0 }} aria-live="polite">
          {readout}
        </Box>
        <Button type="submit" variant="contained" size="small" disabled={!canApply}>
          Apply
        </Button>
      </Box>
    </Box>
  );
}

const TABULAR = { '& input': { fontVariantNumeric: 'tabular-nums' } };

function Shortcuts({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box
      role="group"
      aria-label={label}
      sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.25, mt: 0.25, ml: -0.75 }}
    >
      {children}
    </Box>
  );
}

/** A compact text button whose short visible text ("+1h") is spelled out for
 *  assistive tech and in the tooltip ("To: From + 1 hour"). */
function Shortcut({
  name,
  ...props
}: Omit<ComponentProps<typeof Button>, 'sx' | 'size'> & { name: string }) {
  return (
    <Button
      size="small"
      aria-label={name}
      title={name}
      {...props}
      sx={{ minWidth: 0, px: 0.625, py: 0, fontSize: 13, fontWeight: 500, lineHeight: 1.9 }}
    />
  );
}

/** To = live now: a read-only field-shaped pill, ✕ returns a fixed To set to the
 *  current time. */
function LiveTo({ onFix }: { onFix: () => void }) {
  return (
    <Box
      role="group"
      aria-label="To: now, live"
      sx={{
        height: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        pl: 1.5,
        pr: 0.5,
        border: 1,
        borderColor: 'primary.main',
        borderRadius: 1,
        bgcolor: (t) => (t.palette.mode === 'light' ? 'rgba(10, 26, 63, 0.04)' : 'action.hover'),
      }}
    >
      <UpdateIcon fontSize="small" color="primary" aria-hidden />
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', flex: 1 }}>
        Now · live
      </Typography>
      <Tooltip title="Use a fixed time instead">
        <IconButton size="small" aria-label="Use a fixed time instead" onClick={onFix}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function Problem({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="body2"
      color="error"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 16 }} aria-hidden />
      {children}
    </Typography>
  );
}
